export type RepeatableSaveTarget =
  | 'facility-power'
  | 'rack'
  | 'small-ups'
  | 'large-ups'
  | 'power-distribution'
  | 'cooling'
  | 'liquid-cooling';

export type RepeatableEditorState = {
  facilityPowerLabel: string | null;
  rackOpen: boolean;
  smallUpsOpen: boolean;
  largeUpsOpen: boolean;
  powerDistributionOpen: boolean;
  coolingOpen: boolean;
  liquidCoolingOpen: boolean;
};

export type RepeatableSaveLabels = Record<
  Exclude<RepeatableSaveTarget, 'facility-power'>,
  string
>;

export type RepeatableSaveAction = {
  isOpen: () => boolean;
  commit: () => Promise<boolean>;
};

export type RepeatableSaveActions = Partial<
  Record<RepeatableSaveTarget, RepeatableSaveAction>
>;

/**
 * Resolves editor state only for the step currently rendered by the request
 * screen. Child editor callbacks can outlive their step during navigation, so
 * unscoped booleans are not authoritative enough for footer copy or dispatch.
 */
export function resolveRepeatableSaveTarget(
  stepId: string,
  state: RepeatableEditorState,
): RepeatableSaveTarget | null {
  switch (stepId) {
    case 'facility-power':
      return state.facilityPowerLabel ? 'facility-power' : null;
    case 'rack-power':
      return state.rackOpen ? 'rack' : null;
    case 'small-ups':
      return state.smallUpsOpen ? 'small-ups' : null;
    case 'large-ups':
      return state.largeUpsOpen ? 'large-ups' : null;
    case 'power-distribution':
      return state.powerDistributionOpen ? 'power-distribution' : null;
    case 'cooling-readiness':
      return state.coolingOpen ? 'cooling' : null;
    case 'liquid-cooling':
      return state.liquidCoolingOpen ? 'liquid-cooling' : null;
    default:
      return null;
  }
}

export function resolveRequestFooterSaveLabel(
  target: RepeatableSaveTarget | null,
  facilityPowerLabel: string | null,
  labels: RepeatableSaveLabels,
  fallback: string,
): string {
  if (!target) {
    return fallback;
  }
  if (target === 'facility-power') {
    return facilityPowerLabel ?? fallback;
  }
  return labels[target];
}

/**
 * Commits only the action selected from the displayed step. A missing/closed
 * matching handle is an explicit failure so callers never fall through to a
 * whole-request save while a unit editor is visibly active.
 */
export async function commitRepeatableSaveTarget(
  target: RepeatableSaveTarget,
  actions: RepeatableSaveActions,
): Promise<boolean> {
  const action = actions[target];
  if (!action?.isOpen()) {
    return false;
  }
  return action.commit();
}
